'use client';

import { c as _c } from "react/compiler-runtime";
import Script from 'next/script';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useShallow } from 'zustand/react/shallow';
import { ACCOUNT_ID, ENV, GA_ID, GTM_ID, IS_PRODUCT, PRODUCT_DOMAIN, SUB_DOMAIN } from '@/constants';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';
import { KbAppController, KbHistoryStack } from './kb-pay';
import { AppController, AppModal } from './wello-app';

/**
 * 구글 애널리틱스 설치 코드
 * https://support.google.com/sa360/answer/7566546?hl=ko
 *
 * 아래에 따르면 gtag는 구글 태그 관리자와 함께 사용할 때 필요하지 않음
 * 그러나 현재 gtag() 호출하는 곳이 있어 그냥 둠
 * https://support.google.com/tagmanager/answer/7582054?hl=en
 */
const GA_SCRIPT = {
  __html: `
window.dataLayer = window.dataLayer || [];
function gtag() {
  dataLayer.push(arguments);
}
gtag("js", new Date());
gtag("config", "${GA_ID}");
`
};

/**
 * 구글 태그 관리자 설치 코드
 */
const GTM_SCRIPT = {
  __html: `
(function (w, d, s, l, i) {
  w[l] = w[l] || [];
  w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
  var f = d.getElementsByTagName(s)[0],
    j = d.createElement(s),
    dl = l != "dataLayer" ? "&l=" + l : "";
  j.async = true;
  j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
  f.parentNode.insertBefore(j, f);
})(window, document, "script", "dataLayer", "${GTM_ID}");
`
};
const ClientScript = () => {
  const $ = _c(6);
  const webviewType_0 = useDeviceInfoStore(_temp);
  const [isLogin, memberCode] = useAuthStore(useShallow(_temp2));
  const isMounted = isLogin !== undefined;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      [WEBVIEW_TYPE.KB_PAY]: "KB_PAY_APP",
      [WEBVIEW_TYPE.WELLO_APP]: "WELLO_APP",
      [WEBVIEW_TYPE.TOSS]: "TOSS_APP",
      web: "WELLO_WEB"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = `
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ memberCode: '${memberCode}' });
    window.dataLayer.push({ userEnv: '${t0[webviewType_0 ?? "web"]}' });
    `;
  let t2;
  if ($[1] !== t1) {
    t2 = {
      __html: t1
    };
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const gaDataLayerScript = t2;
  let t3;
  if ($[3] !== gaDataLayerScript || $[4] !== isMounted) {
    t3 = isMounted ? <><Script dangerouslySetInnerHTML={gaDataLayerScript} id="DATA_LAYER" /><Script dangerouslySetInnerHTML={GA_SCRIPT} id="GA" /></> : null;
    $[3] = gaDataLayerScript;
    $[4] = isMounted;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
const GA_SRC = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
export const GlobalScript = () => <>
    <Script async src={GA_SRC} />
    <Script dangerouslySetInnerHTML={GTM_SCRIPT} id="GTM" strategy="afterInteractive" />
    {/* Kakao JavaScript SDK */}
    <Script src="https://t1.kakaocdn.net/kakao_js_sdk/2.7.5/kakao.min.js" integrity="sha384-dok87au0gKqJdxs7msEdBPNnKSRT+/mhTVzq+qOhcL464zXwvcrpjeWvyj1kCdq6" crossOrigin="anonymous" strategy="lazyOnload" onLoad={() => {
    if (window.Kakao && !window.Kakao.isInitialized?.()) {
      window.Kakao.init?.(ENV.NEXT_PUBLIC_KAKAO_CLIENT_ID);
    }
  }} />
    {IS_PRODUCT ? <Script id="META_PIXEL_CODE" strategy="afterInteractive">
        {`
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');

        // Check if pixel is already initialized
        if (!window.fbq.getState || !window.fbq.getState().pixels ||
            !window.fbq.getState().pixels.find(pixel => pixel.id === '846203437117752')) {
          fbq('init', '846203437117752');
        }
        fbq('track', 'PageView');
      `}
      </Script> : null}
    <Script id="boraware-protect-id" strategy="lazyOnload">
      {`var protect_id = 'i559';`}
    </Script>
    <Script src="//script.boraware.kr/protect_script_v2.js" async strategy="lazyOnload" />
    <Script id="smlog-init" strategy="lazyOnload">
      {`
        var hpt_info={'_account':'UHPT-32797', '_server': 'a29'};
      `}
    </Script>
    <Script id="smlog-core" src="//cdn.smlog.co.kr/core/smart.js" strategy="lazyOnload" />
    <noscript>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src="//a29.smlog.co.kr/smart_bda.php?_account=32797" style={{
      display: 'none',
      width: 0,
      height: 0,
      border: 0
    }} alt="" />
    </noscript>

    <Script src="//wcs.naver.net/wcslog.js" strategy="afterInteractive" id="naver-wcslog" onLoad={() => {
    if (!window.wcs) {
      console.error('❌ Naver WCS not available');
      return;
    }
    if (!window.wcs_add) window.wcs_add = {};
    window.wcs_add['wa'] = ACCOUNT_ID;
    window.wcs.inflow(PRODUCT_DOMAIN.replace('https://', ''));

    //* wcs_do가 정의될 때까지 체크하고 이벤트 발생
    let attempts = 0;
    const maxAttempts = 50;
    const checkWcsDoAndDispatch = () => {
      if (typeof window.wcs_do === 'function') {
        window.dispatchEvent(new CustomEvent('naver-wcs-ready'));
      } else {
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkWcsDoAndDispatch, 100);
        } else {
          console.warn('⚠️ wcs_do not found after 5 seconds');
        }
      }
    };
    checkWcsDoAndDispatch();
  }} />
    <Script src="//t1.daumcdn.net/kas/static/kp.js" strategy="lazyOnload" />
    {/* NicePay JavaScript SDK */}
    <Script src="https://pay.nicepay.co.kr/v1/js/" strategy="afterInteractive" id="nicepay-sdk" />
    <Script id="beusable-rum" strategy="lazyOnload">
      {`
        (function(w, d, a){
          w.__beusablerumclient__ = {
            load : function(src){
              var b = d.createElement("script");
              b.src = src; b.async=true; b.type = "text/javascript";
              d.getElementsByTagName("head")[0].appendChild(b);
            }
          };w.__beusablerumclient__.load(a + "?url=" + encodeURIComponent(d.URL));
        })(window, document, "//rum.beusable.net/load/b260408e110801u424");
      `}
    </Script>

    <ClientScript />
  </>;
export const GlobalController = () => {
  const $ = _c(3);
  const subDomain_0 = useDeviceInfoStore(_temp3);
  const webviewType_0 = useDeviceInfoStore(_temp4);
  if (webviewType_0 === WEBVIEW_TYPE.WELLO_APP) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <><AppController /><AppModal /></>;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  switch (subDomain_0) {
    case SUB_DOMAIN.KB_PAY:
      {
        let t0;
        if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
          t0 = <><ErrorBoundary fallback={null}><Suspense><KbHistoryStack /></Suspense></ErrorBoundary><KbAppController /></>;
          $[1] = t0;
        } else {
          t0 = $[1];
        }
        return t0;
      }
    default:
      {
        let t0;
        if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
          t0 = <></>;
          $[2] = t0;
        } else {
          t0 = $[2];
        }
        return t0;
      }
  }
};
function _temp(t0) {
  const {
    webviewType
  } = t0;
  return webviewType;
}
function _temp2(state) {
  return [state.isLogin, state.myInfo?.memberCode];
}
function _temp3(t0) {
  const {
    subDomain
  } = t0;
  return subDomain;
}
function _temp4(t0) {
  const {
    webviewType
  } = t0;
  return webviewType;
}